import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Col, OverlayTrigger, Row, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { createPortal } from 'react-dom'

import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

import { selectCurrentUserEmailIntegration, selectEmailCurrentUser, selectEmailCurrentUserLoading } from '../../store/emailHub/emailUsers'
import { useCreateIntegrationEmailMutation } from '../../store/integrations/integrations'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { addToast } from '../../store/toast/toastSlice'
import {
  selectNewEmailContactLoading,
  selectNewEmailEditing,
  selectNewEmailExecutionLoading,
  selectNewEmailInteractionLoading,
  selectNewEmailRecipientContact,
  selectNewEmailRelatedContact,
  selectNewEmailResult,
  selectShouldPollNewEmailCitations,
  setNewEmailCitationLoading,
  setNewEmailRecipientContact,
  setNewEmailRelatedContact,
} from '../../store/emailHub/newEmail'

import { EmailType, EmailTypeKind } from '../../models/EmailType'
import { User } from '../../models/User'

import { useExportConfirmation } from '../../components/ConfirmationDialog/useExportConfirmation'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { Divider } from '../../components/Divider/Divider'
import { Button } from '../../components/Button/Button'

import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { HeaderContext } from '../LayoutWithLeftSidebar/HeaderContext'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'

import { SendEmailConfirmationModal } from './EmailResponse/SendEmailConfirmationModal'
import { NewEmailInitialization } from './Initialization/NewEmailInitialization'
import { useExecuteNewEmail, useNewEmailResult } from './NewEmail/hooks'
import { NewEmailUserSelector } from './NewEmail/NewEmailUserSelector'
import { EmailTypeSelectorV2 } from './NewEmail/EmailTypeSelectorV2'
import { NoEmailIntegration } from './Common/NoEmailIntegration'
import { NewEmailGenerator } from './NewEmail/NewEmailGenerator'
import { NewEmailEditor } from './NewEmail/NewEmailEditor'
import { NewEmailPoller } from './Poller/NewEmailPoller'
import { NewEmailHeader } from './Header/NewEmailHeader'
import { NewEmailChat } from './NewEmail/NewEmailChat'
import { hasClientRole, isNewEmailNoResult } from './NewEmail/utils'

import './NewEmailPage.scss'

const _USER_SELECTOR_ICON_TOOLTIP_TEXT = 'Select the client this email is about to generate a more relevant email.'

interface NewEmailSendOptions {
  isDraft: boolean
}

export const NewEmailPage = () => {
  const { isFetching: quotasFetching, quotaSuccess, hasQuota } = useZocksQuota()

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const emailEnabled = hasQuota('integration.emailEnabled.value')

  if (!emailEnabled) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['inbox']} />
  }

  return (
    <div className='full-size-content'>
      <NewEmailInitialization />
      <NewEmailPoller />
      <NewEmailPageContent />
    </div>
  )
}

const NewEmailPageContent = () => {
  const dispatch = useAppDispatch()

  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const currentUserLoading = useAppSelector(selectEmailCurrentUserLoading)
  const currentUser = useAppSelector(selectEmailCurrentUser)
  const newEmailResult = useAppSelector(selectNewEmailResult)
  const shouldPollNewEmailCitations = useAppSelector(selectShouldPollNewEmailCitations)
  const executionIsLoading = useAppSelector(selectNewEmailExecutionLoading)
  const interactionIsLoading = useAppSelector(selectNewEmailInteractionLoading)

  const recipientContact = useAppSelector(selectNewEmailRecipientContact)
  const relatedContact = useAppSelector(selectNewEmailRelatedContact)
  const contactLoading = useAppSelector(selectNewEmailContactLoading)

  const [emailType, setEmailType] = useState<EmailType | 'custom' | null>(null)

  const { handleNewEmailExecution } = useExecuteNewEmail()

  const { portalRef } = useContext(HeaderContext)
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (portalRef?.current) {
      setPortalElement(portalRef.current)
    }
  }, [portalRef])

  useEffect(() => {
    if (shouldPollNewEmailCitations) {
      dispatch(setNewEmailCitationLoading(true))
    } else {
      dispatch(setNewEmailCitationLoading(false))
    }
  }, [dispatch, shouldPollNewEmailCitations])

  const handleRecipientContactSelected = (selectedUser: User | null) => {
    const isClient = selectedUser ? hasClientRole(selectedUser) : false
    dispatch(setNewEmailRecipientContact(selectedUser))
    dispatch(setNewEmailRelatedContact(isClient ? selectedUser : null))
    if (!selectedUser) {
      setEmailType(null)
    }
  }

  const handleRelatedContactSelected = (selectedUser: User | null) => {
    dispatch(setNewEmailRelatedContact(selectedUser))
  }

  const handleEmailTypeSelected = (selectedEmailType: EmailType | 'custom' | null) => {
    setEmailType(selectedEmailType)
    if (selectedEmailType && selectedEmailType !== 'custom') {
      handleNewEmailExecution(selectedEmailType)
    }
  }

  if (currentUserLoading || contactLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <div className='skeleton w-100 loading-result-skeleton' style={{ height: '10rem' }} />
      </div>
    )
  }

  if (!currentUser) {
    return null
  }

  if (!emailIntegration) {
    return <NoEmailIntegration />
  }

  return (
    <>
      {portalElement ? createPortal(<NewEmailHeader />, portalElement) : null}
      <Row className='d-flex gap-5'>
        <Col xs={7} className='d-flex flex-column'>
          <Row>
            <Col className='d-flex flex-column zocks-gap-1'>
              <NewEmailUserSelector label='From' initialSelectedUser={currentUser} disabled={true} preventOpenOnMount={true} />
              <Divider />
              <NewEmailUserSelector
                label='To'
                initialSelectedUser={recipientContact}
                onUserSelected={handleRecipientContactSelected}
                placeholder='Select a Contact'
                disabled={executionIsLoading || interactionIsLoading}
                preventOpenOnMount={!!recipientContact}
              />
              <Divider />
              {recipientContact && !hasClientRole(recipientContact) ? (
                <>
                  <NewEmailUserSelector
                    label='Client'
                    IconComponent={NewEmailUserSelectorIconComponent}
                    initialSelectedUser={relatedContact}
                    onUserSelected={handleRelatedContactSelected}
                    placeholder='Select a Client (optional)'
                    onlyClients={true}
                    disabled={executionIsLoading || interactionIsLoading}
                  />
                  <Divider />
                </>
              ) : null}
              {recipientContact ? (
                <>
                  <EmailTypeSelectorV2
                    onEmailTypeSelected={handleEmailTypeSelected}
                    kind={EmailTypeKind.NEW}
                    disabled={executionIsLoading || interactionIsLoading}
                    preventOpenOnMount={!relatedContact}
                  />
                  <Divider />
                </>
              ) : null}
            </Col>
          </Row>
          {emailType ? (
            <Row>
              <Col>
                <div className='zocks-py-2.0'>
                  <NewEmail emailConnector={emailIntegration.serviceId} emailType={emailType} />
                </div>
              </Col>
            </Row>
          ) : null}
        </Col>
        <Col>
          <NewEmailChat headerOnly={!recipientContact || !relatedContact || !emailType || !newEmailResult || isNewEmailNoResult(newEmailResult)} />
        </Col>
      </Row>
    </>
  )
}

const NewEmailUserSelectorIconComponent = () => {
  return (
    <OverlayTrigger placement='top' overlay={<BootstrapTooltip className='zocks-text-gray-500'>{_USER_SELECTOR_ICON_TOOLTIP_TEXT}</BootstrapTooltip>}>
      <i className='bi bi-info-square fs-6' />
    </OverlayTrigger>
  )
}

interface NewEmailProps {
  emailConnector: string
  emailType: EmailType | 'custom'
}

export const NewEmail: React.FC<NewEmailProps> = ({ emailConnector, emailType }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const newEmailResult = useAppSelector(selectNewEmailResult)
  const isEditing = useAppSelector(selectNewEmailEditing)
  const executionIsLoading = useAppSelector(selectNewEmailExecutionLoading)
  const interactionIsLoading = useAppSelector(selectNewEmailInteractionLoading)
  const recipientContact = useAppSelector(selectNewEmailRecipientContact)

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [isSending, setSending] = useState<boolean>(false)

  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation()
  const { newEmailState } = useNewEmailResult()

  const [triggerCreateIntegrationEmail] = useCreateIntegrationEmailMutation()

  const isNoResult = useMemo(() => isNewEmailNoResult(newEmailResult), [newEmailResult])

  const _handleSend = async ({ isDraft }: NewEmailSendOptions) => {
    if (!recipientContact) {
      return
    }

    const { htmlWithoutCitations: body } = newEmailState
    const { subject = '' } = newEmailResult ?? {}

    setSending(true)
    try {
      await triggerCreateIntegrationEmail({
        accountId,
        connector: emailConnector,
        body: {
          recipients: [{ email: recipientContact.email }],
          subject,
          body,
          isDraft,
        },
      }).unwrap()
      const message = isDraft ? 'The email has been saved as draft' : 'The email has been sent'
      dispatch(addToast({ variant: 'success', title: 'Success', messages: [message] }))
      navigate({ pathname: '/email/inbox' })
    } catch (e) {
      console.error(e)
    } finally {
      setSending(false)
      setShowConfirmationModal(false)
    }
  }

  const handleSendWithConfirmation = async ({ isDraft }: NewEmailSendOptions) => {
    const mutationTrigger = async () => await _handleSend({ isDraft })

    await runMutationWithConfirmation(mutationTrigger)
  }

  const handleSend = tracked(
    handleSendWithConfirmation,
    { eventDetails: { connector: emailConnector } },
    {
      eventNameExtractor: ({ isDraft }: NewEmailSendOptions) => {
        return isDraft ? TrackEvent.NEW_EMAIL_PUSHED_TO_DRAFT : TrackEvent.NEW_EMAIL_SENT
      },
    },
  )

  const handleDiscard = () => {
    navigate({ pathname: '/email/inbox' })
  }

  if (executionIsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <div className='skeleton w-100 loading-result-skeleton d-flex align-items-center justify-content-center' style={{ height: '10rem' }}>
          Generating...
        </div>
      </div>
    )
  }

  if (!newEmailResult || isNoResult) {
    return <NewEmailGenerator emailType={emailType} />
  }

  return (
    <>
      {showConfirmationModal ? (
        <SendEmailConfirmationModal
          show={showConfirmationModal}
          isSending={isSending}
          onConfirm={() => handleSend({ isDraft: false })}
          onHide={() => setShowConfirmationModal(false)}
        />
      ) : null}
      <div className='d-flex flex-column zocks-gap-2'>
        <NewEmailEditor />
        <div className='d-flex gap-2 align-items-center justify-content-between'>
          <Button variant='grayGhost' className='button-medium' onClick={handleDiscard}>
            Discard
          </Button>
          <div className='d-flex gap-2 align-items-center'>
            <Button
              variant='primaryGhost'
              className='button-medium'
              onClick={() => handleSend({ isDraft: true })}
              disabled={isEditing || isSending || interactionIsLoading || accountIsLoading}
            >
              <i className='bi bi-inbox' />
              Push to draft
            </Button>
            <Button
              variant='primary'
              className='button-medium'
              onClick={() => setShowConfirmationModal(true)}
              disabled={isEditing || isSending || interactionIsLoading || accountIsLoading}
            >
              <i className='bi bi-send' />
              Send
            </Button>
          </div>
        </div>
      </div>
      <ConfirmationModal />
    </>
  )
}
